/**
 * ============================================================================
 * FHIR → UI mappers
 * ============================================================================
 *
 * Pure functions that reshape FHIR R4 resources (Patient, Condition,
 * MedicationStatement, AllergyIntolerance, Observation) into the row
 * shapes the PatientPanel / ChartTab UI already knows how to render.
 *
 * Tolerant of the usual partial-data realities of HAPI tenants:
 * missing names, missing units, missing dates. Never throws on bad
 * input — returns `null`/empty/'—' so the UI degrades gracefully.
 * ============================================================================
 */

// ── shared helpers ─────────────────────────────────────────────────────────

function entries(bundleOrArray) {
  if (!bundleOrArray) return [];
  if (Array.isArray(bundleOrArray)) return bundleOrArray;
  if (Array.isArray(bundleOrArray.entry)) return bundleOrArray.entry;
  return [];
}

function resourceOf(entry) {
  return entry?.resource ?? entry ?? null;
}

function codingDisplay(codeable) {
  if (!codeable) return null;
  if (codeable.text) return codeable.text;
  const c = (codeable.coding || [])[0];
  return c?.display || c?.code || null;
}

function relativeFrom(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  const diff = Date.now() - t;
  const minutes = Math.round(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 14) return `${days} day${days === 1 ? '' : 's'} ago`;
  const weeks = Math.round(days / 7);
  if (weeks < 8) return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  const months = Math.round(days / 30);
  if (months < 24) return `${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.round(days / 365);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

function ageFrom(birthDate) {
  if (!birthDate) return null;
  const dob = new Date(birthDate);
  if (Number.isNaN(dob.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--;
  return age;
}

// ── Patient ────────────────────────────────────────────────────────────────

export function mapPatient(patient) {
  if (!patient) return null;
  const officialName = (patient.name || []).find((n) => n.use === 'official') ?? (patient.name || [])[0];
  const given = (officialName?.given || []).join(' ').trim();
  const family = officialName?.family?.trim() || '';
  const fullName = [given, family].filter(Boolean).join(' ') || officialName?.text || 'Unknown patient';

  // Prefer an identifier of type 'MR' (Medical Record); fall back to the first identifier.
  const mrnId =
    (patient.identifier || []).find((i) =>
      (i.type?.coding || []).some((c) => c.code === 'MR'),
    ) || (patient.identifier || [])[0];

  return {
    id: patient.id,
    name: fullName,
    mrn: mrnId?.value || patient.id || '—',
    age: ageFrom(patient.birthDate),
    gender: patient.gender || null,
    birthDate: patient.birthDate || null,
    lastUpdated: relativeFrom(patient.meta?.lastUpdated),
  };
}

// ── Conditions (active problems) ───────────────────────────────────────────

export function mapConditions(bundleOrArray) {
  return entries(bundleOrArray)
    .map(resourceOf)
    .filter(Boolean)
    .map((c) => ({
      id: c.id,
      name: codingDisplay(c.code) || 'Untitled condition',
      status:
        codingDisplay(c.clinicalStatus)?.toLowerCase() ||
        (c.clinicalStatus?.coding?.[0]?.code ?? 'active'),
      onsetDate: (c.onsetDateTime || c.onsetPeriod?.start || '').slice(0, 4) || null,
    }));
}

// ── MedicationStatement ────────────────────────────────────────────────────

export function mapMedications(bundleOrArray) {
  return entries(bundleOrArray)
    .map(resourceOf)
    .filter(Boolean)
    .map((m) => {
      const med = m.medicationCodeableConcept;
      const dosage = (m.dosage || [])[0];
      const dose = dosage?.doseAndRate?.[0]?.doseQuantity;
      const doseStr = dose ? `${dose.value ?? ''} ${dose.unit ?? dose.code ?? ''}`.trim() : '';
      return {
        id: m.id,
        name: codingDisplay(med) || m.medicationReference?.display || 'Untitled medication',
        dose: doseStr || dosage?.text || '—',
        frequency: dosage?.timing?.code?.text
          || dosage?.timing?.repeat?.frequency
            ? `${dosage.timing.repeat.frequency}×/${dosage.timing.repeat.periodUnit || 'day'}`
            : (dosage?.text || ''),
        route: dosage?.route ? codingDisplay(dosage.route) || '' : '',
      };
    });
}

// ── AllergyIntolerance ─────────────────────────────────────────────────────

const SEVERITY_RANK = { severe: 3, moderate: 2, mild: 1 };

export function mapAllergies(bundleOrArray) {
  return entries(bundleOrArray)
    .map(resourceOf)
    .filter(Boolean)
    .map((a) => {
      const reaction = (a.reaction || [])[0];
      const manifestation = (reaction?.manifestation || [])[0];
      const severity = (reaction?.severity || '').toLowerCase();
      return {
        id: a.id,
        name: codingDisplay(a.code) || 'Untitled allergen',
        reaction: codingDisplay(manifestation) || reaction?.description || '—',
        severity: SEVERITY_RANK[severity] ? severity : 'unknown',
      };
    });
}

// ── Observations (labs + vitals) ───────────────────────────────────────────

function obsValue(obs) {
  if (obs.valueQuantity) {
    return {
      value: String(obs.valueQuantity.value ?? ''),
      unit: obs.valueQuantity.unit || obs.valueQuantity.code || '',
    };
  }
  if (obs.valueString) return { value: obs.valueString, unit: '' };
  if (obs.valueCodeableConcept) return { value: codingDisplay(obs.valueCodeableConcept) || '—', unit: '' };
  if (obs.component?.length) {
    // Multi-component obs (e.g., BP systolic + diastolic) — join with /
    const parts = obs.component.map((c) => c.valueQuantity?.value).filter((v) => v != null);
    const unit = obs.component[0]?.valueQuantity?.unit || '';
    return { value: parts.join('/'), unit };
  }
  return { value: '—', unit: '' };
}

function obsStatus(obs) {
  // FHIR Observation.interpretation maps to UI status when present.
  const interp = (obs.interpretation || [])[0];
  const code = interp?.coding?.[0]?.code?.toUpperCase();
  if (code === 'H' || code === 'HH') return 'high';
  if (code === 'L' || code === 'LL') return 'low';
  if (code === 'A' || code === 'AA') return 'elevated';
  if (code === 'N') return 'normal';
  return 'normal';
}

export function mapLabs(bundleOrArray) {
  return entries(bundleOrArray)
    .map(resourceOf)
    .filter(Boolean)
    .map((o) => {
      const { value, unit } = obsValue(o);
      return {
        id: o.id,
        test: codingDisplay(o.code) || 'Untitled lab',
        value,
        unit,
        status: obsStatus(o),
        date: relativeFrom(o.effectiveDateTime || o.effectivePeriod?.start || o.issued) || '—',
      };
    });
}

// ── Appointment (TV-P1-02 provider queue) ──────────────────────────────────

/**
 * `priority` on a FHIR Appointment is an int 0-9 with no canonical
 * mapping to triage levels. We bucket it into the queue UI's color
 * codes; if priority is absent the row falls back to 'blue'.
 */
function triageFromPriority(priority) {
  if (priority == null) return 'blue';
  const n = Number(priority);
  if (Number.isNaN(n)) return 'blue';
  if (n <= 1) return 'red';
  if (n <= 3) return 'amber';
  if (n <= 6) return 'blue';
  return 'green';
}

function patientIdFromAppointment(appt) {
  const p = (appt.participant || []).find(
    (part) => part.actor?.reference?.startsWith('Patient/'),
  );
  return p?.actor?.reference?.split('/').slice(-1)[0] || null;
}

/**
 * Map an Appointment resource (+ optional Patient lookup index) into
 * the queue-row shape ProviderQueue expects. The right-panel preview
 * (problems, labs, etc.) lazy-loads via usePatientSummary when a
 * specific row is selected — no per-row patient summary probes here.
 *
 * @param {object} appointment - FHIR Appointment resource
 * @param {Record<string, object>} patients - index of included Patient resources
 */
export function mapAppointment(appointment, patients = {}) {
  if (!appointment) return null;
  const patientId = patientIdFromAppointment(appointment);
  const patient = patientId ? patients[patientId] : null;
  const patientShape = patient ? mapPatient(patient) : null;

  // Wait time = now - appointment.start, but only after the slot has begun.
  let waitTime = 0;
  if (appointment.start) {
    const startMs = Date.parse(appointment.start);
    const diffMin = Math.floor((Date.now() - startMs) / 60_000);
    waitTime = diffMin > 0 ? diffMin : 0;
  }

  // Map FHIR Appointment.status → queue intake bucket the UI tabs by.
  // FHIR canonical statuses: proposed | pending | booked | arrived |
  // fulfilled | cancelled | noshow | entered-in-error | checked-in |
  // waitlist. We collapse to waiting / in-visit / completed.
  const s = (appointment.status || '').toLowerCase();
  let intakeStatus = 'Pending';
  if (s === 'arrived' || s === 'checked-in') intakeStatus = 'In Progress';
  else if (s === 'fulfilled') intakeStatus = 'Complete';
  else if (s === 'booked') intakeStatus = 'Scheduled';

  const queueBucket =
    s === 'fulfilled' ? 'completed'
      : s === 'arrived' || s === 'checked-in' ? 'in-visit'
        : 'waiting';

  return {
    id: appointment.id,
    appointmentId: appointment.id,
    patientId,
    name: patientShape?.name || appointment.description || 'Unknown patient',
    mrn: patientShape?.mrn || '—',
    age: patientShape?.age ?? null,
    chiefComplaint:
      appointment.description
      || (appointment.reasonCode || []).map((r) => codingDisplay(r)).filter(Boolean).join('; ')
      || '—',
    triageLevel: triageFromPriority(appointment.priority),
    waitTime,
    start: appointment.start || null,
    end: appointment.end || null,
    status: s,
    intakeStatus,
    queueBucket,
  };
}

// ── QuestionnaireResponse + CarePlan (TV-P1-09 Assess + Plan tabs) ─────────

const QR_STATUS_LABEL = {
  'in-progress': 'In Progress',
  completed: 'Complete',
  amended: 'Amended',
  'entered-in-error': 'Entered in error',
  stopped: 'Stopped',
};

/**
 * Reshape a QuestionnaireResponse Bundle into Assess-tab rows.
 * Item count is best-effort — flattens nested `item.item` recursively
 * and stops at 1000 to avoid runaway loops on malformed responses.
 */
export function mapQuestionnaireResponses(bundleOrArray) {
  return entries(bundleOrArray)
    .map(resourceOf)
    .filter(Boolean)
    .map((qr) => {
      const totalItems = countItems(qr.item);
      const answeredItems = countItems(qr.item, /*onlyAnswered*/ true);
      // FHIR R4 QuestionnaireResponse.questionnaire is a canonical URL.
      // Strip the URL down to a friendly label; fall back to id / 'Assessment'.
      const url = qr.questionnaire || '';
      const label =
        url ? (url.split('/').slice(-1)[0] || 'Assessment') : 'Assessment';
      return {
        id: qr.id,
        name: label.replace(/[-_]/g, ' '),
        status: qr.status,
        statusLabel: QR_STATUS_LABEL[qr.status] || qr.status || '—',
        completedItems: answeredItems,
        totalItems,
        createdAt: relativeFrom(qr.authored) || qr.authored?.slice(0, 10) || '—',
        rawAuthored: qr.authored || null,
      };
    });
}

function countItems(items, onlyAnswered = false, depth = 0) {
  if (!Array.isArray(items) || depth > 8) return 0;
  let n = 0;
  for (const it of items) {
    if (onlyAnswered) {
      if (Array.isArray(it.answer) && it.answer.length > 0) n++;
    } else {
      n++;
    }
    if (Array.isArray(it.item)) n += countItems(it.item, onlyAnswered, depth + 1);
    if (n > 1000) break;
  }
  return n;
}

/**
 * Reshape a single CarePlan's activity[] entries into Plan-tab rows.
 * Each activity may be inline detail OR a reference to another
 * resource (ServiceRequest, MedicationRequest, Task, Appointment).
 * We surface inline-detail directly; for refs we show display + the
 * resourceType so the row is still meaningful.
 */
export function mapCarePlanActivities(carePlan) {
  if (!carePlan || !Array.isArray(carePlan.activity)) return [];
  return carePlan.activity.map((act, idx) => {
    // Inline activity.detail — the more common shape for self-contained plans.
    if (act.detail) {
      const d = act.detail;
      const text =
        d.description
        || codingDisplay(d.code)
        || 'Plan activity';
      // FHIR R4 CarePlanActivityKind: ServiceRequest | MedicationRequest |
      // Appointment | etc. Used to pick the icon flavor in the UI.
      const kind = d.kind || codingDisplay(d.code) || 'Activity';
      return {
        id: `inline-${idx}`,
        text,
        kind,
        status: d.status || 'unknown',
      };
    }
    // Reference activity — point at the underlying resource.
    if (act.reference?.display || act.reference?.reference) {
      const text = act.reference.display
        || `${act.reference.reference}`;
      const kind = act.reference.reference?.split('/')?.[0] || 'Activity';
      return {
        id: `ref-${idx}`,
        text,
        kind,
        status: 'referenced',
      };
    }
    return {
      id: `unknown-${idx}`,
      text: 'Plan activity (no detail or reference)',
      kind: 'Activity',
      status: 'unknown',
    };
  });
}

export function mapVitals(bundleOrArray) {
  return entries(bundleOrArray)
    .map(resourceOf)
    .filter(Boolean)
    .map((o) => {
      const { value, unit } = obsValue(o);
      return {
        id: o.id,
        name: codingDisplay(o.code) || 'Untitled vital',
        value,
        unit,
        status: obsStatus(o),
        timestamp:
          relativeFrom(o.effectiveDateTime || o.effectivePeriod?.start || o.issued) || '—',
      };
    });
}
